import React from 'react';
import { Box, Typography, Card, CardContent, Grid, Chip } from '@mui/material';
import { Link } from 'react-router-dom';
import { 
  Code, 
  Architecture, 
  School, 
  Forum, 
  Dashboard,
  Notes,
  TrendingUp
} from '@mui/icons-material';

const RelatedLinks = ({ currentPage }) => {
  const linkData = {
    dsa: {
      title: "Continue Your Learning Journey",
      links: [
        {
          path: "/lld",
          title: "Low Level Design Practice",
          description: "Master design patterns and OOPS concepts",
          icon: <Architecture />,
          keywords: "design patterns, SOLID principles"
        },
        {
          path: "/hld",
          title: "High Level Design Practice", 
          description: "Learn system architecture and scalability",
          icon: <TrendingUp />,
          keywords: "system design, scalability"
        },
        {
          path: "/discussions",
          title: "Join Community Discussions",
          description: "Connect with fellow developers",
          icon: <Forum />,
          keywords: "interview experiences, Q&A"
        }
      ]
    },
    lld: {
      title: "Expand Your System Design Skills",
      links: [
        {
          path: "/dsa",
          title: "DSA Practice Questions",
          description: "Strengthen your algorithmic thinking",
          icon: <Code />,
          keywords: "data structures, algorithms"
        },
        {
          path: "/hld",
          title: "High Level Design",
          description: "Scale up to system architecture",
          icon: <TrendingUp />,
          keywords: "distributed systems, microservices"
        },
        {
          path: "/deep-dives",
          title: "Technical Deep Dives",
          description: "Explore advanced CS concepts",
          icon: <School />,
          keywords: "advanced topics, system internals"
        }
      ]
    },
    hld: {
      title: "Complete Your System Design Mastery",
      links: [
        {
          path: "/lld",
          title: "Low Level Design",
          description: "Master the building blocks",
          icon: <Architecture />,
          keywords: "design patterns, object oriented design"
        },
        {
          path: "/dsa",
          title: "DSA Practice",
          description: "Strengthen algorithmic foundation",
          icon: <Code />,
          keywords: "algorithms, data structures"
        },
        {
          path: "/discussions",
          title: "System Design Discussions",
          description: "Learn from real experiences",
          icon: <Forum />,
          keywords: "system design interviews, case studies"
        }
      ]
    },
    discussions: {
      title: "Practice What You Learn",
      links: [
        {
          path: "/dsa",
          title: "Practice DSA Problems",
          description: "Apply concepts in coding challenges",
          icon: <Code />,
          keywords: "coding practice, problem solving"
        },
        {
          path: "/lld",
          title: "Design System Components",
          description: "Practice low level design",
          icon: <Architecture />,
          keywords: "system components, design patterns"
        },
        {
          path: "/dashboard",
          title: "Track Your Progress",
          description: "Monitor your learning journey",
          icon: <Dashboard />,
          keywords: "progress tracking, analytics"
        }
      ]
    },
    'deep-dives': {
      title: "Apply Advanced Concepts",
      links: [
        {
          path: "/hld",
          title: "System Architecture Practice",
          description: "Apply concepts in real scenarios",
          icon: <TrendingUp />,
          keywords: "system architecture, scalable design"
        },
        {
          path: "/discussions",
          title: "Discuss Complex Topics",
          description: "Engage with the community",
          icon: <Forum />,
          keywords: "technical discussions, expert insights"
        },
        {
          path: "/myNotes",
          title: "Save Important Concepts",
          description: "Keep track of key learnings",
          icon: <Notes />,
          keywords: "note taking, knowledge management"
        }
      ]
    },
    dashboard: {
      title: "Start Your Practice",
      links: [
        {
          path: "/dsa",
          title: "Begin with DSA",
          description: "Start with fundamental concepts",
          icon: <Code />,
          keywords: "beginner DSA, coding fundamentals"
        },
        {
          path: "/lld",
          title: "Learn System Design",
          description: "Understand software architecture",
          icon: <Architecture />,
          keywords: "system design basics, software architecture"
        },
        {
          path: "/discussions",
          title: "Connect with Community",
          description: "Learn from others' experiences",
          icon: <Forum />,
          keywords: "learning community, peer support"
        }
      ]
    }
  };

  const currentData = linkData[currentPage];
  
  if (!currentData) return null;

  return (
    <Box sx={{ mt: 6, mb: 4 }}>
      <Typography variant="h4" sx={{ mb: 3, textAlign: 'center', fontWeight: 'bold', color: '#333' }}>
        {currentData.title}
      </Typography>
      
      <Grid container spacing={3}>
        {currentData.links.map((link, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card 
              component={Link}
              to={link.path}
              sx={{ 
                height: '100%',
                textDecoration: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                }
              }}
            >
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Box sx={{ color: '#008080', mb: 2 }}>
                  {React.cloneElement(link.icon, { sx: { fontSize: 40 } })}
                </Box>
                
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#333' }}>
                  {link.title}
                </Typography>
                
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  {link.description}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                  {link.keywords.split(', ').map((keyword, idx) => (
                    <Chip 
                      key={idx}
                      label={keyword}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default RelatedLinks;





